import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { Item } from 'src/app/shared/fd-form-components/fd-select/fd-select.component';
import { environment } from 'src/environments/environment';
import { MccModel, MccPhysicalPersonDeleteModel, MccPhysicalPersonHistoryModel, MccPhysicalPersonModel, MccPhysicalPersonSaveModel, SearchMccPhysicalModel, SearchMccPhysicalPersonHistoryModel } from '../models/mcc-physical-person.model';
import { Pageable } from 'src/app/shared/models/pageable.model';
import { CamelCaseParams, UrlParamsUtils } from 'src/app/shared/utils/url-params.utils';

@Injectable({
  providedIn: 'root',
})
export class MccService {

  constructor(private http: HttpClient) { }

  getMccList(institution: string): Observable<Item[]> {

    const url = `${environment.apiUrls.mccPhysicalPerson}/mccs`;

    const filterParams = { 
        'institution': institution, 
        'size': 400
    }
 
    const urlParams = new UrlParamsUtils(new CamelCaseParams()).buildUrlParams(
      filterParams);

    return this.http.get<Pageable<MccModel[]>>(url, {
      params: urlParams
    }).pipe(
      map((pageable: Pageable<MccModel[]>) =>
      (pageable.response || []).sort((a, b) => Number(a.mcc) - Number(b.mcc))
      ),
      map((sortedList: MccModel[]) => this.mapToMccModels(sortedList))
    );
  
  }

  saveMccPhysicalPerson(model: MccPhysicalPersonSaveModel) {
    return this.http.post<MccPhysicalPersonSaveModel>(`${environment.apiUrls.mccPhysicalPerson}`,model);
  }

  getMccPhysicalPersonHistory(filterValue: SearchMccPhysicalPersonHistoryModel) {
    const urlParams = new UrlParamsUtils(new CamelCaseParams()).buildUrlParams(
      filterValue);

    return this.http.get<Pageable<MccPhysicalPersonHistoryModel[]>>(`${environment.apiUrls.mccPhysicalPerson}/history`, {params: urlParams})
    .pipe(map(x => this.mapToHistoryMccPhysicalPersonModels(x.response)));
  }

  getPhysicalPersonsMccs( filterValue: SearchMccPhysicalModel) {
    const {serviceContract, ...formattedFilter} = filterValue;
    
    const filterParams = {...formattedFilter, 
        'service-contract': filterValue.serviceContract
    }
 
    const urlParams = new UrlParamsUtils(new CamelCaseParams()).buildUrlParams(
      filterParams);
    
    return this.http.get<Pageable<MccPhysicalPersonModel[]>>(`${environment.apiUrls.mccPhysicalPerson}`, {
      params: urlParams
    });
  }

  deleteMcc(filterValue: MccPhysicalPersonDeleteModel){ 
    return this.http.post<any>(`${environment.apiUrls.mccPhysicalPerson}/delete`, filterValue);
  }
  
  
  private mapToHistoryMccPhysicalPersonModels(data:MccPhysicalPersonHistoryModel[]): MccPhysicalPersonHistoryModel[] {
    if (!data) {
      return [];
    }
    return data.map(value => ({
          name: value.name,
          cpfCnpj: value.cpfCnpj,
          status: value.status,
          createdDate: value.createdDate,
          institution: value.institution,
          serviceContract: value.serviceContract,
          mcc: value.mcc,
          mccDescription: value.mccDescription
    }));
  }

  private mapToMccModels(response: MccModel[]): Item[] {
    if (!response) {
      return [];
    }
    return response.map(value => {
      
      let label: string;
      const description = value.merchantTariffDescription?.toString() ?? '';
      
      if (!description.trim()) {
        label = value.mcc;
      } else if (/^\d{4}\s*-\s*/.test(description)) {
        label = value.mcc + ' - ' + description.replace(/^\d{4}\s*-\s*/, '').trim();
      } else {
        label = value.mcc + ' - ' + description.trim();
      }
      return {
        value: value.mcc,
        label,
      };
    });
  }
  
}